import { Component, HostListener, OnInit } from '@angular/core';
import { FeatureCollection } from 'geojson';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Feature } from 'ol';
import { Geometry, MultiPoint } from 'ol/geom';
import { PointsService } from 'src/app/services/points.service';
import GeoJSON from 'ol/format/GeoJSON';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { GlobalSelectionControlService } from 'src/app/map/services/global-selection-control.service';
import { Layer, VectorLayerDMTR } from 'src/app/menu/view-menu/layer';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { ConfigurationLayerModel } from 'src/app/models/configuration';
import { Select } from 'ol/interaction';
import { EventListenersOnMap } from 'src/app/utils/eventListenersOnMap';
import { Extent } from 'ol/extent';
import { MapService } from 'src/app/services/map.service';
import { CoordinatePointModel } from 'src/app/models/coordinate-point';
import { coordinatePointsSelectedLayer } from 'src/app/menu/view-menu/layersList';

@Component({
  selector: 'app-select-by-fir',
  templateUrl: './select-by-fir.component.html',
  styleUrls: ['../../app.component.scss'],
  standalone: false
})
export class SelectByFirComponent implements OnInit {

  firLayersList: ConfigurationLayerModel[] = [];
  selectedFirLayer: ConfigurationLayerModel;

  readonly entitiesList: string[] = [
    "Navaids",
    "Coordinate Points",
    "Procedures",
    "Air Space Boundaries"
  ]

  selectedEntity: string = this.entitiesList[0];

  public static readonly EVENT_ON_MAP_LABEL: string = "Selection by FIR";

  constructor
    (
    private readonly bsModalRef: BsModalRef,
    private readonly pointsService: PointsService,
    private readonly globalSelectionControlService: GlobalSelectionControlService,
    private readonly modalMessageService: ModalControlService,
    private readonly configurationService: ConfigurationService,
    private readonly mapService: MapService,
  )
  {

  }
  ngOnInit(): void {
    for (const staticLayer of this.configurationService.currentConfiguration.layers) {
      if (staticLayer.name.toLowerCase().includes("fir") && staticLayer.enabled) {
        this.firLayersList.push(staticLayer);
      }
    }

    if (this.firLayersList.length == 0) {
      this.close();
      this.modalMessageService.showMesssageModal("Selection by FIR", "No FIR layer enabled on static layers.");
      return;
    }

    this.selectedFirLayer = this.firLayersList[0];
  }


  setSelectionEvent() {
    EventListenersOnMap.RemoveEventListener(SelectByFirComponent.EVENT_ON_MAP_LABEL);

    if (!this.selectedFirLayer.visible) {
      this.mapService.activateStaticLayer(this.selectedFirLayer);
    }
    this.mapService.moveLayersToFront([this.selectedFirLayer.vectorLayer]);

    const visibleEntityLayers = this.getVisibleEntityLayers(this.selectedEntity);
    this.mapService.moveLayersToFront(this.getEntitiesLayers(visibleEntityLayers));

    const select: Select = new Select({
      style: null,
      layers: [this.selectedFirLayer.vectorLayer.olLayer],
    })


    this.mapService.clearSelections();

    EventListenersOnMap.addSelectEventListener(SelectByFirComponent.EVENT_ON_MAP_LABEL, select,
      (event) => {
        if (event.selected.length > 0) {
          this.selectIntersectionWithFirFeature(event.selected[0], this.selectedEntity);
        }

        if (event.deselected.length > 0) {
          for (const feature of event.deselected) {
            this.selectIntersectionWithFirFeature(feature, this.selectedEntity, false);
          }
        }
      },
      true
    )

    this.close();
  }

  getEntitiesLayers(selectedEntity): Layer[] {
    switch (selectedEntity) {
      case "Navaids":
        return this.pointsService.navaidsLayers.childLayers[0].layers;
      case "Coordinate Points":
        return [coordinatePointsSelectedLayer];
      case "Procedures":
        return this.pointsService.proceduresLayers.childLayers[0].layers;
      case "Air Space Boundaries":
        return this.pointsService.airspaceboundariesLayers.childLayers[0].layers;
    }
  }

  selectIntersectionWithFirFeature(firFeature: Feature, selectedEntity: string, select: boolean = true) {

    if (selectedEntity == "Coordinate Points" && select) {
      const cpLayers = this.getVisibleEntityLayers("Coordinate Points");
      this.selectCoordinatePoints(firFeature, cpLayers)
      return;
    }

    const selectedLayers: Layer[] = this.getVisibleEntityLayers(selectedEntity);

    // Filter entities that intersect with the FIR
    const firGeometry: Geometry = firFeature.getGeometry();
    const firExtent: Extent = firGeometry.getExtent(); // Get bounding box
    for (const layer of selectedLayers) {
      const entitiesVectorSource = layer.olLayer.getSource();
      entitiesVectorSource.forEachFeatureIntersectingExtent(firExtent, (feature) => {
        if (firGeometry.intersectsExtent(feature.getGeometry().getExtent())) {
          if (select) {
            this.globalSelectionControlService.setSelectedFeature(layer.olLayer, feature);
          } else {
            this.globalSelectionControlService.removeSelectedFeature(feature);
          }
        }
      })
    }
  }


  selectCoordinatePoints(firFeature: Feature, cpLayers: Layer[]) {
    const firGeometry: Geometry = firFeature.getGeometry();
    for (const cpLayer of (cpLayers || []).filter(this.isLayerCurrentlyVisible)) {
      const vectorSource = cpLayer.olLayer.getSource();
      vectorSource.forEachFeature((feature) => {
        const geometry = feature.getGeometry();
        if (geometry instanceof MultiPoint) {
          const coordinates = geometry.getCoordinates();
          coordinates.forEach((coordinate) => {
            if (firGeometry.intersectsCoordinate(coordinate)) {
              const pointFeature = this.pointsService.getCoordinatePointFeatureFromLayersByCoordinate(
                coordinate,
                feature.get(CoordinatePointModel.LAYER_ID_FEATURE_PROPERTY),
                feature.get(CoordinatePointModel.PUBLISHED_FEATURE_PROPERTY)
              );
              this.globalSelectionControlService.setSelectedCoordinatePointFeature(pointFeature);
            }
          });
        }
      });
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent) {
    this.resetDefaults(); // escape key already hides the modal
  }

  close(): void {
    this.resetDefaults();
    this.bsModalRef.hide();
  }

  resetDefaults() {
    this.selectedFirLayer = null;
    this.firLayersList = [];
    this.selectedEntity = this.entitiesList[0];
  }

  private isLayerCurrentlyVisible = (l: Layer) =>
    !!l &&
    (l.visible !== false) &&
    (typeof l.olLayer?.getVisible === 'function' ? l.olLayer.getVisible() : true) &&
    (typeof l.olLayer?.getOpacity === 'function' ? l.olLayer.getOpacity() !== 0 : true);

  private getEntityParentLayers(entity: string) {
    switch (entity) {
      case "Navaids": return this.pointsService.navaidsLayers;
      case "Coordinate Points": return this.pointsService.coordinatePointsLayers;
      case "Procedures": return this.pointsService.proceduresLayers;
      case "Air Space Boundaries": return this.pointsService.airspaceboundariesLayers;
    }
  }

  private getVisibleEntityLayers(entity: string): Layer[] {
    const parent = this.getEntityParentLayers(entity);
    if (!parent?.childLayers?.length) return [];
    const layers: Layer[] = [];
    parent.childLayers.forEach(ch => {
      ch.layers.forEach(l => { if (this.isLayerCurrentlyVisible(l)) layers.push(l); });
    });
    return layers;
  }


}
